import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Random variables: the plank the rest of the course walks on.
 *
 * Probability up to here has been about *events* — things that either happen or
 * do not. A random variable is the move that turns probability into arithmetic:
 * attach a number to each outcome, and suddenly the whole apparatus of means
 * and standard deviations applies to something that has not happened yet.
 *
 * The five lessons are deliberately not one. `expected-value`,
 * `random-variable-variance` and `random-variable-sd` could be taught in a
 * single sitting as three formulas, and are separated because each is a
 * different *idea* wearing a formula: an average of things that did not happen,
 * a spread measured in squared units nobody can interpret, and the return to
 * real units that finally makes the number comparable to the data. The last of
 * those carries the rule the whole inference half of the course runs on —
 * variances add for independent variables and standard deviations do not — and
 * as a footnote to a combined lesson it would be read once and lost.
 *
 * `continuous-random-variables` closes the unit and deliberately uses no
 * calculus. Probability as area under a density, and P(X = x) = 0, are both
 * completely visible on a rectangle and a triangle; requiring integration would
 * gate this course behind the calculus pathway for a technique it never uses,
 * since every normal area in the course comes from a table.
 */
export const RANDOM_VARIABLE_LESSONS: Lesson[] = [
  lesson({
    id: 'discrete-random-variables-01',
    unitId: 'unit-random-variables',
    conceptId: 'discrete-random-variables',
    title: 'Discrete Random Variables',
    summary: 'Attaching a number to every outcome, so that chance becomes something you can do arithmetic to.',
    objectives: [
      'Build the probability distribution of a discrete random variable from a sample space',
      'Check a distribution is valid and use it to find P(X ≤ k)',
      'Distinguish the random variable X from a value x it can take',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'From events to numbers',
        blocks: [
          text('Toss three coins. The sample space has eight entries — HHH, HHT, HTH, and so on — and every question so far has been about *events*: the event of getting two heads, the event of getting at least one tail.'),
          text('Now count the heads. Each of the eight outcomes gets a number: 3, 2, 2, 1, 2, 1, 1, 0. That counting rule is a **random variable**, written with a capital letter:'),
          math('X = \\text{the number of heads in three tosses}'),
          insight('A random variable is a function from the sample space to the numbers. It is not random and it is not a variable in the algebraic sense — it is a fixed rule, applied to an outcome that has not been decided yet. The randomness is in the outcome; X merely reads it off.'),
          text('The payoff is immediate. Numbers can be averaged, squared, added to other numbers and compared. Events cannot. Every technique in the rest of this course — expected value, variance, the four named distributions, every confidence interval — exists because someone attached numbers to a sample space.'),
        ],
      },
      intuition: {
        title: 'The distribution is the whole object',
        blocks: [
          text('A **discrete** random variable takes values you can list: 0, 1, 2, 3 … To describe it completely you need the probability of each, and that list is its **probability distribution**.'),
          table(
            ['x', '0', '1', '2', '3'],
            [['P(X = x)', '1/8', '3/8', '3/8', '1/8']],
          ),
          text('Two conditions make a list of numbers a distribution, and both are the axioms in new clothes:'),
          list(
            [
              'Every probability is between 0 and 1.',
              'They total exactly 1 — X takes *some* value every time.',
            ],
            true,
          ),
          warning('Capital X is the variable; lowercase x is a value it might take. P(X = 2) is a number; "P(x)" says nothing, because it does not say which. The distinction looks pedantic until a question asks for P(X ≤ 2) and you need to know what is being accumulated.', 'X and x are not the same letter'),
          text('Once you have the distribution, three kinds of question are all just addition over the table:'),
          list([
            '**Exactly** — P(X = 2) = 3/8, one entry read off.',
            '**At most** — P(X ≤ 2) = 1/8 + 3/8 + 3/8 = 7/8, entries accumulated. This is the **cumulative** probability.',
            '**At least** — P(X ≥ 2) = 3/8 + 1/8 = 4/8, or 1 − P(X ≤ 1), whichever is shorter.',
          ]),
          warning('"At least 2" includes 2. "More than 2" does not. On a discrete variable these are genuinely different sums, and swapping them is the single most common arithmetic error in the topic — it is why the boundary value is worth circling before you add anything.', 'The boundary belongs to one side'),
          insight('Notice what the distribution threw away. It records that two heads has probability 3/8; it does not record *which* three outcomes those were. That loss is the point — everything the rest of the course needs is in the table, and the sample space can be forgotten once the table exists.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 1 },
                { label: '1', value: 3 },
                { label: '2', value: 3 },
                { label: '3', value: 1 },
              ],
              unitLabel: 'eighths of probability, by number of heads',
            },
            'The distribution of X, drawn. The bars total 8 eighths — the "totals 1" condition, made visible. It is symmetric because heads and tails are interchangeable on a fair coin.',
          ),
          text('The same information as a cumulative picture, which is what an "at most" question is reading:'),
          table(
            ['x', 'P(X = x)', 'P(X ≤ x)'],
            [
              ['0', '1/8', '1/8'],
              ['1', '3/8', '4/8'],
              ['2', '3/8', '7/8'],
              ['3', '1/8', '8/8 = 1'],
            ],
          ),
          tip('The last entry of a cumulative column must be 1. If it is not, the distribution is wrong and no answer computed from it can be right — check that before anything else.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A game charges nothing to play. You roll one fair die; let X be the score you keep, which is the number rolled, except that a 1 scores 0. Write the distribution of X and find P(X ≥ 4) and P(1 < X ≤ 4).'),
          steps([
            { label: 'List the outcomes and the value X gives each.', latex: '1 \\to 0,\\; 2 \\to 2,\\; 3 \\to 3,\\; 4 \\to 4,\\; 5 \\to 5,\\; 6 \\to 6' },
            { label: 'Each face has probability 1/6, so each value inherits it.', latex: 'P(X = 0) = P(X = 2) = \\cdots = P(X = 6) = \\tfrac{1}{6}' },
            { label: 'Check the total.', latex: '6 \\times \\tfrac{1}{6} = 1', note: 'Six values, not seven — X never takes the value 1.' },
            { label: 'P(X ≥ 4): the values 4, 5 and 6.', latex: '\\tfrac{1}{6} + \\tfrac{1}{6} + \\tfrac{1}{6} = \\tfrac{1}{2}' },
            { label: 'P(1 < X ≤ 4): strictly above 1, up to and including 4.', latex: '\\tfrac{1}{6} + \\tfrac{1}{6} + \\tfrac{1}{6} = \\tfrac{1}{2}', note: 'The values 2, 3 and 4. X = 1 is impossible anyway.' },
          ]),
          text('The re-labelling in step 1 is what a random variable is *for*. Nothing about the die changed; a rule was laid over it, and the resulting distribution is no longer the flat 1/6-across-six-faces that a die usually has — it is flat across a different set of numbers, with a gap at 1 and an extra value at 0.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('discrete-random-variables', 4)] },
      independent: { title: 'On your own', blocks: [practice('discrete-random-variables', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('discrete-random-variables', 3, 'Distributions built from a sample space, and cumulative probabilities with sharp boundaries.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A random variable is a rule attaching a number to every outcome — X is the rule, x is a value.',
            'Its probability distribution lists P(X = x) for every value, and those probabilities total exactly 1.',
            '"At most" accumulates from the bottom; "at least" from the top; the boundary value belongs to one of them and not the other.',
            'P(X ≥ k) is often quickest as 1 − P(X ≤ k − 1).',
            'The distribution keeps everything later work needs and forgets the sample space it came from.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'expected-value-01',
    unitId: 'unit-random-variables',
    conceptId: 'expected-value',
    title: 'Expected Value',
    summary: 'The long-run average of something that has not happened yet — and why it is usually a value that cannot happen at all.',
    objectives: [
      'Compute E(X) as a probability-weighted sum',
      'Interpret an expected value as a long-run average, not a prediction',
      'Use E(aX + b) = aE(X) + b to shift and scale a random variable',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'An average of what has not happened',
        blocks: [
          text('A raffle sells 500 tickets at £2 each. One ticket wins £400, ten win £20. Is a ticket worth buying?'),
          text('The question is about an average, but not an average of a list of numbers you have — an average of the numbers you *might* get, weighted by how likely each is. That is the **expected value**:'),
          math('E(X) = \\sum x \\cdot P(X = x)'),
          insight('Every value the variable can take, multiplied by its probability, added up. The probabilities are the weights, which is why this is sometimes called the *weighted mean* — and why treating the outcomes as equally likely, and taking a plain average, is the standard way to get it wrong.'),
          text('For the raffle: (400 × 1/500) + (20 × 10/500) + (0 × 489/500) = 0.80 + 0.40 = £1.20 of expected winnings for a £2 ticket. The organisers keep 80p per ticket, which is what a raffle is.'),
        ],
      },
      intuition: {
        title: 'What the number is, and what it is not',
        blocks: [
          text('E(X) is also written μ, and that is not an accident of notation: it is the **mean of the distribution**, in exactly the sense that the mean of a data set is its balance point. Draw the distribution as bars on a beam and E(X) is where the beam balances.'),
          warning('The expected value is very often a value the variable can never take. The expected number of heads in three tosses is 1.5; the expected score on a die is 3.5. "Expected" is a technical word meaning "long-run average", not a prediction of what will happen. Nobody expects half a head.', '"Expected" does not mean likely'),
          text('The claim it *does* make is about the long run: repeat the experiment many times, average what you get, and the average closes in on E(X). That is the law of large numbers, and it is the reason casinos and insurers can be confident about their year while knowing nothing about your evening.'),
          text('Two properties make expected values usable, and both are worth more than the definition:'),
          math('E(aX + b) = a\\,E(X) + b'),
          math('E(X + Y) = E(X) + E(Y)'),
          list([
            'Scaling and shifting pass straight through. Double every prize and the expected winnings double; add £5 to every prize and the expectation rises by exactly £5.',
            'Expectations of a sum add — **always**, whether or not X and Y are independent. This is the one place in the course where independence is not required, and it is why the mean of a binomial is np without any work.',
          ]),
          insight('The "add £5 to every prize" case shows why the +b is not multiplied. Every single outcome moved up by 5, so the balance point moved up by 5 — a shift of the whole distribution does not care about probabilities at all.'),
          text('A **fair** game is one where E(profit) = 0. Almost no real game is fair; the expected profit is the house edge, and it is a per-play figure that says nothing about any single play.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 1 },
                { label: '1', value: 3 },
                { label: '2', value: 3 },
                { label: '3', value: 1 },
              ],
              unitLabel: 'eighths of probability — heads in three tosses',
            },
            'The balance point of these four bars is 1.5, halfway between the two tall ones. That is E(X), and it lands in the gap between the values 1 and 2, where the variable can never be.',
          ),
          text('The same calculation, laid out as the weighted sum it is:'),
          table(
            ['x', 'P(X = x)', 'x · P(X = x)'],
            [
              ['0', '1/8', '0'],
              ['1', '3/8', '3/8'],
              ['2', '3/8', '6/8'],
              ['3', '1/8', '3/8'],
              ['**Total**', '**1**', '**12/8 = 1.5**'],
            ],
          ),
          tip('Always add the probability column. If it does not come to 1, the expected value below it is meaningless — and this check costs one addition.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('An insurer sells a £150 policy. There is a 2% chance of a £2000 claim and a 5% chance of a £400 claim. Find the insurer’s expected profit per policy, and the expected profit if the premium rises to £180.'),
          steps([
            { label: 'Let X be the amount paid out. List its distribution.', latex: 'P(X = 2000) = 0.02, \\quad P(X = 400) = 0.05, \\quad P(X = 0) = 0.93' },
            { label: 'Expected payout: weight each value.', latex: 'E(X) = 2000(0.02) + 400(0.05) + 0(0.93) = 40 + 20 = 60' },
            { label: 'Profit is the premium minus the payout — a shift and a sign.', latex: 'P = 150 - X' },
            { label: 'Apply E(aX + b) = aE(X) + b with a = −1, b = 150.', latex: 'E(P) = 150 - E(X) = 150 - 60 = \\pounds 90' },
            { label: 'At £180 the premium shifts by 30, so the expectation does too.', latex: 'E(P) = 180 - 60 = \\pounds 120' },
          ]),
          text('Step 4 is where the linearity earns its keep: nobody recomputed a distribution for the profit. And step 5 shows the shift rule doing what it says — every possible profit rose by exactly £30, so the average did too, with no reference to the probabilities.'),
          warning('The unweighted average of 2000, 400 and 0 is 800, and it is not the expected payout of anything. It answers "what is the mean of these three numbers", which would only be the right question if a £2000 claim were as likely as no claim at all.', 'Not the average of the outcomes'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('expected-value', 4)] },
      independent: { title: 'On your own', blocks: [practice('expected-value', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('expected-value', 3, 'Fair games, linear transformations, and expectations that no outcome can equal.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'E(X) = Σ x·P(X = x): every value weighted by its probability.',
            'It is the mean μ of the distribution — the balance point of its bars.',
            '"Expected" means long-run average, and the value is often impossible on any single trial.',
            'E(aX + b) = aE(X) + b: scaling scales it, shifting shifts it.',
            'E(X + Y) = E(X) + E(Y) whether or not the variables are independent.',
            'A game is fair when the expected profit is zero, which nearly none are.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'random-variable-variance-01',
    unitId: 'unit-random-variables',
    conceptId: 'random-variable-variance',
    title: 'Variance',
    summary: 'How far a random variable typically lands from its mean — squared on purpose, and left that way for now.',
    objectives: [
      'Compute Var(X) from the definition and from E(X²) − μ²',
      'Explain why the deviations are squared before averaging',
      'Apply Var(aX + b) = a²Var(X)',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two games with the same expectation',
        blocks: [
          text('Game A pays £9 or £11, each with probability 1/2. Game B pays £0 or £20, each with probability 1/2. Both have an expected value of £10, and nobody would call them the same game.'),
          insight('The expected value is a single number and it answers a single question: where is the centre? It says nothing about how far from that centre the variable actually lands, and for anyone deciding whether to play, that second question is the one that matters.'),
          text('**Variance** answers it, and its definition is the same weighted average as before, applied to the squared distance from the mean:'),
          math('\\operatorname{Var}(X) = E\\big[(X - \\mu)^{2}\\big] = \\sum (x - \\mu)^{2}\\,P(X = x)'),
          text('Game A: (−1)²(½) + (1)²(½) = 1. Game B: (−10)²(½) + (10)²(½) = 100. A hundred times the variance, for the same expected payout.'),
        ],
      },
      intuition: {
        title: 'Why squared, and the formula you will actually use',
        blocks: [
          text('The obvious measure of spread is the average distance from the mean. It is useless, and it is useless for a reason worth understanding:'),
          math('E(X - \\mu) = E(X) - \\mu = \\mu - \\mu = 0'),
          insight('The signed deviations always total zero — that is what makes μ the balance point in the first place. Any measure of spread has to destroy the signs before averaging, and squaring is the way that keeps the algebra workable. (Taking absolute values also works and gives a different, less tractable, measure.)'),
          text('Squaring has a second consequence, and it is a design choice rather than a side effect: a deviation of 10 contributes a hundred times as much as a deviation of 1. Variance is deliberately dominated by the rare large misses, which is exactly the behaviour an insurer or an engineer wants from a risk measure.'),
          text('Computing from the definition means finding μ first, then sweeping the table a second time. The **computational formula** does it in one sweep:'),
          math('\\operatorname{Var}(X) = E(X^{2}) - \\mu^{2}'),
          text('where E(X²) = Σx²P(X = x) — square the *values*, not the probabilities, and weight as usual.'),
          warning('E(X²) and [E(X)]² are different numbers, and the whole formula is the gap between them. E(X²) squares first and averages second; μ² averages first and squares second. They are equal only when the variable is constant — which is to say, only when the variance is zero.', 'Square then average, or average then square'),
          text('And the transformation rule, which is the reason variance is worth carrying around in squared units at all:'),
          math('\\operatorname{Var}(aX + b) = a^{2}\\operatorname{Var}(X)'),
          list([
            'The **+b vanishes**. Shifting every value by the same amount moves the whole distribution and changes no distance within it.',
            'The **a is squared**, because the units are squared. Double every value and the distances double, so their squares quadruple.',
          ]),
          insight('A negative a disappears too: Var(−X) = Var(X). Spread has no direction. This matters in the next lesson, where the variance of a *difference* turns out to be a sum.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '9', value: 5 },
                { label: '10', value: 0 },
                { label: '11', value: 5 },
              ],
              unitLabel: 'Game A — probability in tenths',
            },
            'Game A: both outcomes hug the mean of 10. Variance 1.',
          ),
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 5 },
                { label: '10', value: 0 },
                { label: '20', value: 5 },
              ],
              unitLabel: 'Game B — probability in tenths',
            },
            'Game B: the same mean of 10, the same two bars, ten times further out. Variance 100 — the squaring is why a tenfold change in distance is a hundredfold change in variance.',
          ),
          text('The computational formula on Game B, in a table:'),
          table(
            ['x', 'P(X = x)', 'x·P', 'x²·P'],
            [
              ['0', '0.5', '0', '0'],
              ['20', '0.5', '10', '200'],
              ['**Total**', '**1**', '**μ = 10**', '**E(X²) = 200**'],
            ],
          ),
          math('\\operatorname{Var}(X) = E(X^{2}) - \\mu^{2} = 200 - 100 = 100'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('X is the number of faults in a component, with P(0) = 0.5, P(1) = 0.3, P(2) = 0.15 and P(3) = 0.05. Find Var(X), and then the variance of the repair cost C = 40X + 15.'),
          steps([
            { label: 'Find μ = E(X).', latex: '\\mu = 0(0.5) + 1(0.3) + 2(0.15) + 3(0.05) = 0.75' },
            { label: 'Find E(X²) — square the values, keep the probabilities.', latex: 'E(X^{2}) = 0(0.5) + 1(0.3) + 4(0.15) + 9(0.05) = 1.35' },
            { label: 'Subtract the square of the mean.', latex: '\\operatorname{Var}(X) = 1.35 - 0.75^{2} = 1.35 - 0.5625 = 0.7875' },
            { label: 'The cost is a linear transformation: a = 40, b = 15.', latex: '\\operatorname{Var}(C) = 40^{2} \\times 0.7875 = 1600 \\times 0.7875 = 1260' },
          ]),
          text('The £15 call-out fee in step 4 is charged on every visit, so it moves every possible cost up by the same amount and cannot change how spread out the costs are. The £40 per fault multiplies the distances by 40, and the variance — being in squared pounds — by 1600.'),
          warning('A variance can never be negative, and getting one is the reliable sign that E(X²) and μ² have been swapped, or that the probabilities did not total 1. Check both before rewriting anything else.', 'A negative variance is an arithmetic error'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('random-variable-variance', 4)] },
      independent: { title: 'On your own', blocks: [practice('random-variable-variance', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('random-variable-variance', 3, 'E(X²) − μ², and variances under a linear transformation.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Var(X) = E[(X − μ)²]: the probability-weighted average of squared distances from the mean.',
            'Squaring is needed because the signed deviations always total zero.',
            'The working formula is Var(X) = E(X²) − μ², and E(X²) ≠ [E(X)]².',
            'Var(aX + b) = a²Var(X): the shift does nothing, the scale is squared.',
            'Var(−X) = Var(X) — spread has no direction.',
            'Variance is in squared units, which is why the next lesson exists.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'random-variable-sd-01',
    unitId: 'unit-random-variables',
    conceptId: 'random-variable-sd',
    title: 'Standard Deviation and Combining Variables',
    summary: 'Getting the units back — and the rule everybody gets wrong: variances add, standard deviations do not.',
    objectives: [
      'Find σ from the variance and interpret it in the units of the variable',
      'Combine independent random variables using Var(X ± Y) = Var(X) + Var(Y)',
      'Explain why the variance of a difference is a sum',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Squared pounds',
        blocks: [
          text('The repair cost in the last lesson had a variance of 1260. Twelve hundred and sixty what? Squared pounds, which is not a quantity anybody has ever needed.'),
          text('The **standard deviation** is the square root of the variance, and its entire job is to put the answer back into the units of the thing being measured:'),
          math('\\sigma = \\sqrt{\\operatorname{Var}(X)}'),
          text('√1260 ≈ £35.50, and that is a number a person can use: repairs cost about £45 on average, give or take £35. It is directly comparable to the values of X, which the variance never was.'),
          insight('σ is the measure you quote and Var is the measure you compute with. Every combination rule in statistics is stated in variances, because variances add and standard deviations do not — and that single fact is what this lesson is really about.'),
        ],
      },
      intuition: {
        title: 'The rule that is wrong in the obvious form',
        blocks: [
          text('Two independent random variables are added. What happens to the centre and to the spread?'),
          math('E(X + Y) = E(X) + E(Y) \\quad \\text{(always)}'),
          math('\\operatorname{Var}(X + Y) = \\operatorname{Var}(X) + \\operatorname{Var}(Y) \\quad \\text{(independent only)}'),
          warning('Standard deviations do **not** add. σ(X + Y) = √(σ²ₓ + σ²ᵧ), which is smaller than σₓ + σᵧ. Two independent measurements each with σ = 3 combine to σ = √18 ≈ 4.24, not 6. Adding the standard deviations directly overstates the spread every time.', 'The Pythagorean rule, not the arithmetic one'),
          insight('The formula is Pythagoras, and so is the intuition. Independent errors point in "different directions" and partly cancel: it is unlikely that both variables land high at the same time. Only errors that move together — perfectly correlated ones — would add along a straight line.'),
          text('Now the rule that looks like a typo and is not:'),
          math('\\operatorname{Var}(X - Y) = \\operatorname{Var}(X) + \\operatorname{Var}(Y)'),
          text('A **plus**, for a difference. It follows from the previous lesson: X − Y is X + (−Y), and Var(−Y) = (−1)²Var(Y) = Var(Y). Subtracting a variable does not subtract its uncertainty — it adds a second source of it.'),
          warning('This is the error that survives into professional work. The difference of two measurements is *more* uncertain than either, because both were uncertain. Var(X − Y) = Var(X) − Var(Y) can even produce a negative variance, which is the arithmetic announcing the mistake.', 'A difference is noisier, not quieter'),
          text('One more distinction, because the notation hides it. **2X** and **X₁ + X₂** are not the same thing:'),
          table(
            ['', 'Meaning', 'Mean', 'Variance'],
            [
              ['2X', 'one observation, doubled', '2μ', '4σ²'],
              ['X₁ + X₂', 'two independent observations, added', '2μ', '2σ²'],
            ],
          ),
          insight('Doubling one measurement doubles its error too. Taking two measurements and adding them lets the errors partly cancel, so the spread grows by √2 rather than by 2. This is the entire reason a larger sample is better, and it returns as σ/√n when sampling distributions arrive.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Two independent variables, each with σ = 3, combined three ways:'),
          table(
            ['Quantity', 'Variance', 'Standard deviation'],
            [
              ['X alone', '9', '3'],
              ['X + Y', '9 + 9 = 18', '≈ 4.24'],
              ['X − Y', '9 + 9 = 18', '≈ 4.24'],
              ['2X', '4 × 9 = 36', '6'],
              ['*wrong*: σₓ + σᵧ', '—', '6'],
            ],
          ),
          text('The last two rows are the trap laid side by side. Adding the standard deviations of two independent variables gives 6 — the same answer as doubling *one* of them, which is the calculation that assumes the two always move together.'),
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 3, y: 0, label: 'σₓ = 3' },
                { x: 0, y: 3, label: 'σᵧ = 3', from: { x: 3, y: 0 } },
                { x: 3, y: 3, label: 'σ(X+Y) ≈ 4.24', dashed: true },
              ],
              min: -1,
              max: 6,
            },
            'Why the rule is Pythagorean. Independent spreads combine at right angles, so the resultant is √(3² + 3²) ≈ 4.24 — not the 6 you would get by laying them end to end along one line.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A machine fills jars: the contents have mean 500 g and standard deviation 4 g, and the empty jar has mean 60 g and standard deviation 3 g, independently. Find the mean and standard deviation of the total weight, and of the difference between two filled jars.'),
          steps([
            { label: 'Means always add.', latex: 'E(C + J) = 500 + 60 = 560\\text{ g}' },
            { label: 'Convert both spreads to variances before combining.', latex: '\\operatorname{Var}(C) = 16, \\quad \\operatorname{Var}(J) = 9' },
            { label: 'Independent, so the variances add.', latex: '\\operatorname{Var}(C + J) = 16 + 9 = 25' },
            { label: 'Back to a standard deviation.', latex: '\\sigma = \\sqrt{25} = 5\\text{ g}', note: 'Not 4 + 3 = 7.' },
            { label: 'Now two filled jars, T₁ and T₂, each with σ = 5. Their difference:', latex: '\\operatorname{Var}(T_1 - T_2) = 25 + 25 = 50' },
            { label: 'And its mean and standard deviation.', latex: 'E(T_1 - T_2) = 0, \\quad \\sigma = \\sqrt{50} \\approx 7.07\\text{ g}' },
          ]),
          text('The last two steps are the counter-intuitive pair. Two jars from the same machine differ by 0 g on average, and yet the difference is *more* variable than either jar — σ ≈ 7.07 against 5 — because a heavy jar paired with a light one produces a large difference, and either jar can play either role.'),
          tip('The procedure never varies: square to get variances, combine, square-root at the end. Every mistake in this topic is a combination performed on the standard deviations directly.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('random-variable-sd', 4)] },
      independent: { title: 'On your own', blocks: [practice('random-variable-sd', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('random-variable-sd', 3, 'Sums, differences, and the gap between 2X and X₁ + X₂.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'σ = √Var(X), and it is the only one of the two in the units of the data.',
            'Var(X + Y) = Var(X) + Var(Y) for independent variables; σ(X + Y) = √(σ²ₓ + σ²ᵧ).',
            'Var(X − Y) = Var(X) + Var(Y) as well — a difference is noisier than either variable.',
            'Means add for a sum and subtract for a difference, independence or not.',
            '2X has variance 4σ²; X₁ + X₂ has variance 2σ². They are different quantities.',
            'Always combine variances, and take the square root last.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'continuous-random-variables-01',
    unitId: 'unit-random-variables',
    conceptId: 'continuous-random-variables',
    title: 'Continuous Random Variables',
    summary: 'A quantity with no gaps between its values, where probability is an area and any single value has none.',
    objectives: [
      'Explain why P(X = x) = 0 for a continuous random variable',
      'Find a probability as an area under a density curve',
      'Use the total-area-of-1 condition to complete a density',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A bus that arrives at 3.7 minutes past',
        blocks: [
          text('A bus is equally likely to arrive at any moment in the next 10 minutes. What is the probability it arrives at exactly 3.7 minutes past?'),
          text('Zero. And at exactly 4 minutes past? Also zero. And at exactly 3.700000001? Zero again. If every single value has probability zero, and something has to happen, where did the probability go?'),
          insight('It went into *intervals*. For a **continuous** random variable there are uncountably many possible values, so no single one can carry a positive share of the total. Probability attaches to ranges — "between 3 and 4 minutes" has probability 0.1 — and to nothing smaller.'),
          text('This is not a technicality to be waved past. It changes what a distribution looks like (a curve, not a table), how a probability is computed (an area, not a sum), and one piece of arithmetic that discrete work required constant care over: with P(X = x) = 0, the boundaries stop mattering.'),
        ],
      },
      intuition: {
        title: 'Density, area, and the boundary that stops mattering',
        blocks: [
          text('A continuous variable is described by a **probability density function** f(x) — a curve with two properties:'),
          list(
            [
              'It is never negative.',
              'The total area beneath it is exactly 1.',
            ],
            true,
          ),
          text('And the probability of landing in an interval is the area above that interval:'),
          math('P(a \\le X \\le b) = \\text{area under } f \\text{ from } a \\text{ to } b'),
          warning('The height of the curve is **not** a probability. A density can exceed 1 — a variable confined to [0, 0.5] must have a density averaging 2 — and asking for f(3) as though it were P(X = 3) is the standard misreading. Only areas are probabilities.', 'Height is not probability'),
          insight('Because a single point has zero width, it has zero area, and therefore P(X = a) = 0. Which means P(X < a) = P(X ≤ a): strict and non-strict inequalities agree. The boundary care that discrete distributions demanded is simply unnecessary here — one of the few places where the harder-looking object is easier to work with.'),
          text('Two densities do almost all the teaching work, because their areas are shapes anyone can compute:'),
          table(
            ['Density', 'Shape', 'Probability of an interval'],
            [
              ['Uniform on [0, 10]', 'rectangle of height 0.1', 'width ÷ 10'],
              ['Triangular on [0, 4]', 'triangle of height 0.5', '½ × base × height of the piece'],
              ['Normal', 'bell curve', 'from a table — no formula by hand'],
            ],
          ),
          text('The mean of a continuous variable is still the balance point of the shape, and the standard deviation still measures typical distance from it. For a symmetric density the mean is the axis of symmetry, which is how every normal question in the next unit locates μ without any calculation.'),
          insight('Where does the sum go? Σx·P(X = x) becomes an integral, ∫x·f(x)dx, in a calculus course. Nothing in this course needs it: every mean here is read off a symmetry, and every normal area comes from a table.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'density',
              points: [
                { x: 0, y: 0 },
                { x: 0, y: 0.1 },
                { x: 10, y: 0.1 },
                { x: 10, y: 0 },
              ],
              shade: { from: 3, to: 6, label: '0.3' },
              marks: [0, 3, 6, 10],
              unitLabel: 'minutes until the bus arrives',
              label: 'uniform on [0, 10]',
            },
            'A uniform density: a rectangle of height 0.1 across a width of 10, so the total area is 1. The shaded strip from 3 to 6 has area 3 × 0.1 = 0.3, and that is P(3 ≤ X ≤ 6).',
          ),
          text('The rectangle makes the two rules concrete. Total area 1 forces the height: a variable spread evenly over 10 minutes must have density 0.1, because 10 × 0.1 = 1. Over 4 minutes the height would be 0.25 — a taller curve for a narrower range, since the same probability has less room to occupy.'),
          figure(
            {
              kind: 'density',
              points: [
                { x: 0, y: 0 },
                { x: 4, y: 0.5 },
                { x: 4, y: 0 },
              ],
              shade: { from: 2, to: 4, label: '0.75' },
              marks: [0, 2, 4],
              unitLabel: 'a triangular density on [0, 4]',
              label: 'sloping, still area',
            },
            'A density need not be flat. Here it rises to a height of 0.5 — comfortably above 1 would also be legal — and the whole triangle has area ½ × 4 × 0.5 = 1. The shaded piece from 2 to 4 is three quarters of that area, so P(X ≥ 2) = 0.75, even though the interval is only half the range.',
          ),
          figure(
            {
              kind: 'normal-curve',
              mean: 0,
              sd: 1,
              shade: { from: -1, to: 1, label: 'an area, not a height' },
              label: 'the density that matters most',
            },
            'The normal density, whose areas cannot be found by geometry at all — hence the tables of the next unit. The principle is identical: the shaded region is a probability, the curve’s height is not.',
          ),
          tip('For any density made of straight lines, every probability question is an area of a rectangle or a triangle. Draw the shape, mark the interval, compute the area — no new technique is involved.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A waiting time T, in minutes, has a density that is constant at height h on [0, 8] and zero elsewhere. Find h, then P(T ≤ 3), P(T > 5) and P(T = 3).'),
          steps([
            { label: 'The total area must be 1, and the shape is a rectangle.', latex: '8h = 1 \\Rightarrow h = 0.125' },
            { label: 'P(T ≤ 3): a rectangle of width 3.', latex: '3 \\times 0.125 = 0.375' },
            { label: 'P(T > 5): the width from 5 to 8 is 3.', latex: '3 \\times 0.125 = 0.375', note: 'Or 1 − P(T ≤ 5) = 1 − 0.625.' },
            { label: 'P(T = 3): a strip of zero width.', latex: '0' },
            { label: 'Check the mean by symmetry.', latex: 'E(T) = 4', note: 'The balance point of a rectangle is its middle.' },
          ]),
          text('Step 4 is the one to sit with. It does not say the bus cannot arrive at 3 minutes — it says the probability of that exact real number, out of a continuum of them, is zero. Impossible and probability-zero are different things once the values stop being countable.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('continuous-random-variables', 4)] },
      independent: { title: 'On your own', blocks: [practice('continuous-random-variables', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('continuous-random-variables', 3, 'Densities completed from the area rule, and probabilities as areas.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A continuous random variable is described by a density f(x) ≥ 0 whose total area is 1.',
            'Probability is the area under the curve over an interval, never the height of the curve.',
            'P(X = x) = 0, so strict and non-strict inequalities give the same answer.',
            'A density may exceed 1; only areas are capped at 1.',
            'For a symmetric density the mean is the axis of symmetry.',
            'Uniform and triangular densities reduce every question to the area of a rectangle or a triangle.',
          ]),
        ],
      },
    },
  }),
];
